import { useEffect, useState, useMemo, type FormEvent } from 'react';
import { cn } from '@/lib/utils';
import { usePhoneAuth } from './usePhoneAuth';
import { normalizePhone, defaultCountryFromLocale } from './normalize';

export interface PhoneAuthSuccessPayload {
  firebaseToken: string;
  phoneE164: string;
}

interface PhoneAuthFlowProps {
  onSuccess: (payload: PhoneAuthSuccessPayload) => void;
  onCancel?: () => void;
  /** A number to start with, e.g. the one an invitation was texted to. */
  defaultPhone?: string;
}

export function PhoneAuthFlow({ onSuccess, onCancel, defaultPhone }: PhoneAuthFlowProps) {
  const { state, handlers } = usePhoneAuth();
  const defaultCountry = useMemo(() => defaultCountryFromLocale(), []);
  const [phoneInput, setPhoneInput] = useState(defaultPhone ?? '');
  // The number can arrive after this mounts; fill it in unless they've typed.
  useEffect(() => {
    if (defaultPhone) setPhoneInput(current => current || defaultPhone);
  }, [defaultPhone]);
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSendCode = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const normalized = normalizePhone(phoneInput, defaultCountry);
    if (!normalized) {
      setLocalError('Please enter a valid phone number including country code.');
      return;
    }
    handlers.sendCode(normalized.e164);
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (code.length < 6) {
      setLocalError('Enter the 6-digit code.');
      return;
    }
    const token = await handlers.verifyCode(code);
    if (token && state.phoneE164) {
      onSuccess({ firebaseToken: token, phoneE164: state.phoneE164 });
    }
  };

  const errorToShow = localError || state.error;
  const busy = state.isSending || state.isVerifying;

  return (
    <div className="w-full">
      {state.step === 'enter-phone' && (
        <form onSubmit={handleSendCode} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p className="lp-prose" style={{ fontSize: 13 }}>
            We'll text you a 6-digit code to verify your number.
          </p>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="Phone (e.g. +1 555 123 4567)"
            value={phoneInput}
            onChange={e => setPhoneInput(e.target.value)}
            required
            disabled={busy}
            className="lp-input"
          />
          {errorToShow && <div className="lp-error">{errorToShow}</div>}
          <button
            type="submit"
            disabled={busy}
            className={cn('lp-btn lp-btn-primary', busy && 'opacity-70')}
            aria-busy={busy}
          >
            {state.isSending ? (
              <>
                <span className="lp-spinner-sm" /> Sending code…
              </>
            ) : (
              'Send code'
            )}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="lp-btn-link"
              style={{ alignSelf: 'center', fontSize: 12 }}
            >
              Use email instead
            </button>
          )}
        </form>
      )}

      {state.step === 'enter-code' && (
        <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p className="lp-prose" style={{ fontSize: 13 }}>
            Code sent to <span style={{ color: 'var(--lp-ink)' }}>{state.phoneE164}</span>.{' '}
            <button
              type="button"
              onClick={() => {
                handlers.reset();
                setCode('');
              }}
              className="lp-btn-link"
              style={{ fontSize: 12 }}
            >
              wrong number?
            </button>
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            disabled={busy}
            autoFocus
            className="lp-input"
            style={{ textAlign: 'center', letterSpacing: '0.4em' }}
          />
          {errorToShow && <div className="lp-error">{errorToShow}</div>}
          <button
            type="submit"
            disabled={busy}
            className={cn('lp-btn lp-btn-primary', busy && 'opacity-70')}
            aria-busy={busy}
          >
            {state.isVerifying ? (
              <>
                <span className="lp-spinner-sm" /> Verifying…
              </>
            ) : (
              'Verify'
            )}
          </button>
          <button
            type="button"
            onClick={() => handlers.resendCode()}
            disabled={state.resendCooldown > 0 || state.isSending}
            className="lp-btn-link"
            style={{ alignSelf: 'center', fontSize: 12, opacity: state.resendCooldown > 0 ? 0.5 : 1 }}
          >
            {state.resendCooldown > 0 ? `resend in ${state.resendCooldown}s` : 'resend code'}
          </button>
        </form>
      )}

    </div>
  );
}

export default PhoneAuthFlow;
